import { AuthService } from '@core/services/auth.service';
import { UiLanguageSwitchService } from '@core/services/ui-language-switch.service';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HighlightLoader } from 'ngx-highlightjs';
import { MatToolbar } from '@angular/material/toolbar';
import { AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import type { ConsoleUiLang } from '@shared/functions/console-locale-href.function';
import { snackbars, snackbarsMsg } from '@workers';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatButton,
    RouterLink,
    MatSlideToggle,
    FormsModule,
    ReactiveFormsModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
  ],
})
export class NavComponent implements OnInit {
  private authService = inject(AuthService);

  private router = inject(Router);

  private uiLanguageSwitch = inject(UiLanguageSwitchService);

  private readonly snackBar = inject(MatSnackBar);

  @Input() windowMode: 'large' | 'medium' | 'small';

  @Output() menuMenuBtnToggle = new EventEmitter();

  toggleControl = new FormControl(false);

  user$ = this.authService.user$;

  private readonly hljsLoader: HighlightLoader = inject(HighlightLoader);

  ngOnInit(): void {
    this.toggleControl.valueChanges.subscribe((value) => {
      if (value) {
        document.documentElement.classList.add('is-dark-mode');
        document.documentElement.classList.remove('is-light-mode');
      } else {
        document.documentElement.classList.add('is-light-mode');
        document.documentElement.classList.remove('is-dark-mode');
      }
      this.hljsLoader.setTheme(
        value ? 'assets/atom-one-dark.css' : 'assets/atom-one-light.css'
      );
    });

    const darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.toggleControl.setValue(darkMode);

    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        this.toggleControl.setValue(e.matches);
      });
  }

  handleMenuBtnToggle(): void {
    this.menuMenuBtnToggle.emit();
  }

  switchUiLanguage(lang: ConsoleUiLang): void {
    this.uiLanguageSwitch.switchTo(lang).subscribe({
      error: () => {
        this.snackBar.open(snackbarsMsg.updateFailed, '', snackbars.error);
      },
    });
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
